import { useEffect, useState } from 'react';
import { adminSupabase } from '@/integrations/supabase/adminClient';

export interface ComplianceQueueCounts {
  heldIntents: number;
  openCases: number;
}

/**
 * Lightweight polling hook for compliance sidebar badges.
 * Counts held payment intents and open compliance cases.
 * Refreshes every 60s.
 */
export function useComplianceQueueCounts(): ComplianceQueueCounts {
  const [counts, setCounts] = useState<ComplianceQueueCounts>({
    heldIntents: 0,
    openCases: 0,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [held, cases] = await Promise.all([
          adminSupabase
            .from('payment_intents')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'held_for_review'),
          adminSupabase
            .from('compliance_cases')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'open'),
        ]);
        if (cancelled) return;
        setCounts({
          heldIntents: held.count ?? 0,
          openCases: cases.count ?? 0,
        });
      } catch {
        /* swallow — badges are best-effort */
      }
    };

    void load();
    const t = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  return counts;
}
